import { Video } from '@remotion/media'
import React, { useMemo } from 'react'
import { Sequence, useCurrentFrame, useVideoConfig } from 'remotion'
import { useAssets, useMediaAsset } from '../contexts'
import { useCroppableLayer } from '../crop'
import { useFadeOpacity, volumeFn } from '../fades'
import { ColorBackdrop, resolveFit, useBlurBackdrop } from '../fit'
import { itemFramePieces } from '../sceneWindow'
import { MEDIA_PREMOUNT_SECONDS } from './premount'
import type { VideoItem } from '../types'

export function VideoLayer({
  item,
  trackMuted,
  cropBackground,
}: {
  item: VideoItem
  trackMuted: boolean
  cropBackground: boolean
}) {
  const frame = useCurrentFrame()
  const { fps, durationInFrames } = useVideoConfig()
  const asset = useMediaAsset(item.assetId)
  const assets = useAssets()
  // The kept parts of the scene's window (#77): one piece without a cut.
  const pieces = useMemo(
    () => itemFramePieces(item, assets, fps),
    [item, assets, fps],
  )

  const volume = useMemo(
    () =>
      volumeFn({
        fps,
        audioFadeInDurationInSeconds: item.audioFadeInDurationInSeconds,
        audioFadeOutDurationInSeconds: item.audioFadeOutDurationInSeconds,
        durationInFrames: item.durationInFrames,
        decibelAdjustment: item.decibelAdjustment,
      }),
    [
      fps,
      item.audioFadeInDurationInSeconds,
      item.audioFadeOutDurationInSeconds,
      item.durationInFrames,
      item.decibelAdjustment,
    ],
  )

  const opacity = useFadeOpacity({
    frame,
    fps,
    durationInFrames,
    fadeInDurationInSeconds: item.fadeInDurationInSeconds,
    fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
    opacity: item.opacity,
  })

  const { innerStyle, outerStyle } = useCroppableLayer({
    item,
    rotation: item.rotation,
    opacity,
    borderRadius: item.borderRadius,
    cropBackground,
    // The new media tag takes objectFit as a prop, not CSS.
    includeObjectFitInStyle: false,
  })

  if (!asset.remoteUrl) throw new Error(`Asset ${asset.id} has no URL`)

  const src = asset.remoteUrl
  // Fill or Fit (#547); Auto letterboxes a video whose aspect is not its box's.
  const fit = resolveFit(item, asset.type === 'video' ? asset : null)
  const video = (trimBefore: number, offset: number) => (
    <FittedVideo
      // Fades are the item's: a piece's frames count from its own start.
      volume={offset ? (f: number) => volume(f + offset) : volume}
      trimBefore={trimBefore}
      src={src}
      style={innerStyle}
      fit={fit}
      backdrop={item.backdrop}
      box={item}
      muted={trackMuted}
      playbackRate={item.playbackRate}
    />
  )

  return (
    <div style={outerStyle}>
      {pieces.length === 1
        ? video(pieces[0]!.trimBefore, 0)
        : pieces.map((piece) => (
            // Premounted, so the next piece is loaded before the cut.
            <Sequence
              key={piece.from}
              from={piece.from}
              durationInFrames={piece.durationInFrames}
              premountFor={MEDIA_PREMOUNT_SECONDS * fps}
            >
              {video(piece.trimBefore, piece.from)}
            </Sequence>
          ))}
    </div>
  )
}

/**
 * One piece of the scene with its backdrop behind it. A blur is drawn from
 * this piece's own frames onto its own canvas, inside its `<Sequence>`, so
 * a premounted piece never paints the one on screen.
 */
function FittedVideo({
  fit,
  backdrop,
  box,
  ...props
}: {
  volume: (frame: number) => number
  trimBefore: number
  src: string
  style: React.CSSProperties
  fit: 'cover' | 'contain'
  backdrop: VideoItem['backdrop']
  box: { width: number; height: number }
  muted: boolean
  playbackRate: number
}) {
  const blurred = fit === 'contain' && backdrop?.kind !== 'color'
  const blur = useBlurBackdrop(box, blurred)
  return (
    <>
      {fit === 'contain' && backdrop?.kind === 'color' ? (
        <ColorBackdrop color={backdrop.color} />
      ) : null}
      {blurred ? blur.canvas : null}
      <Video
        {...props}
        objectFit={fit}
        onVideoFrame={blurred ? blur.onVideoFrame : undefined}
      />
    </>
  )
}
